import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { GET_ACTION_DEFINITION, getActionDefinition } from 'automation-ui-action-features/actions/extension/extensionQueries';
import { ExternalDataLabel } from 'automation-ui-action-features/actions/extension/render-to-string/ExternalDataLabel';
import FieldString from 'automation-ui-action-features/actions/extension/render-to-string/FieldString';
import convertFieldDataToDataToken from 'automation-ui-action-features/actions/extension/utils/convertFieldDataToDataToken';
import { getConvertedFieldType } from 'automation-ui-action-features/actions/extension/utils/getConvertedFieldType';
import { getDependentFields } from 'automation-ui-action-features/actions/extension/utils/getDependentFields';
import { applyInterpolation } from 'automation-ui-action-features/actions/extension/utils/interpolation-utils';
import SkeletonText from 'automation-ui-core/components/SkeletonText';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import { useQuery } from 'data-fetching-client';
import { Text } from 'foundations-components/components/Text';
import get from 'immutable-less/transmute/get';
import { jsx as _jsx } from "react/jsx-runtime";
const fieldKeyRegex = /\{\{(.+?)\}\}/g; // Example: {{ someFieldKey }}

const findField = (fields, fieldKey) => fields ? fields.find(field => field.fieldKey === fieldKey) : null;
const getStringValues = genericActionField => {
  var _genericActionField$f;
  const maybeStringValues = (_genericActionField$f = genericActionField.fieldValue) === null || _genericActionField$f === void 0 ? void 0 : _genericActionField$f.getValue();
  return Array.isArray(maybeStringValues) ? maybeStringValues : undefined;
};
const ExtensionActionDetail = ({
  action,
  actionMetadata,
  flowObjectTypeId,
  getActionLabelById,
  getActionOutputsForTargetActionById
}) => {
  const actionTypeId = actionMetadata.getActionTypeId();
  const {
    data,
    loading
  } = useQuery(GET_ACTION_DEFINITION, {
    skip: !actionTypeId,
    variables: {
      id: actionTypeId
    }
  });
  if (loading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      minHeight: 20,
      grow: true
    });
  }
  const actionDefinition = getActionDefinition(data);
  if (!actionDefinition) {
    return null;
  }
  const renderFieldLabel = fieldKey => {
    const metadataFields = actionMetadata.getFields();
    const genericActionField = findField(metadataFields, fieldKey);
    const fieldDefinition = actionDefinition.inputFields.find(inputField => inputField.typeDefinition.name === fieldKey);
    if (!genericActionField || !fieldDefinition) {
      return `{{ ${fieldKey} }}`;
    } else if (fieldDefinition.typeDefinition.optionsUrl) {
      const {
        name
      } = fieldDefinition.typeDefinition;
      const dependentFields = getDependentFields({
        fieldDependencies: actionDefinition.inputFieldDependencies,
        fieldKey,
        metadataFields
      }).map(value => value.toJSON());
      return /*#__PURE__*/_jsx(ExternalDataLabel, {
        actionTypeId: actionDefinition.actionTypeId,
        dependentFields: dependentFields,
        fieldName: name,
        inputActionName: genericActionField.inputActionName || undefined,
        objectTypeId: flowObjectTypeId,
        value: genericActionField.fieldValue.getValue()
      });
    } else if (fieldDefinition.supportedValueTypes) {
      const [firstSupportedValueType] = fieldDefinition.supportedValueTypes;
      if (firstSupportedValueType === 'OBJECT_PROPERTY') {
        const {
          fieldValue
        } = genericActionField;
        if (!fieldValue) return null;
        switch (fieldValue.valueType) {
          case 'PROPERTY':
            return /*#__PURE__*/_jsx(PropertyName, {
              objectTypeId: flowObjectTypeId,
              propertyName: fieldValue.propertyName
            });
          case 'FIELD_DATA':
            {
              var _fieldValue$fieldData, _fieldValue$fieldData2, _fieldValue$fieldData3;
              const targetActionId = action.actionId;
              const actionOutputsForTargetActionById = targetActionId ? getActionOutputsForTargetActionById(targetActionId) : [];
              const sourceActionId = (_fieldValue$fieldData = fieldValue.fieldDataIdentifier) === null || _fieldValue$fieldData === void 0 ? void 0 : _fieldValue$fieldData.actionId;
              const inputSource = actionOutputsForTargetActionById.find(({
                actionId
              }) => actionId === sourceActionId);
              if (!inputSource || !sourceActionId) {
                return /*#__PURE__*/_jsx(FormattedReactMessage, {
                  message: "automation-ui-action-features.action-features.extensions.action-detail.data-passing",
                  options: {
                    sourceField: /*#__PURE__*/_jsx(SkeletonText, {}),
                    sourceValue: /*#__PURE__*/_jsx(SkeletonText, {})
                  }
                });
              }
              const sourceName = (_fieldValue$fieldData2 = (_fieldValue$fieldData3 = fieldValue.fieldDataIdentifier) === null || _fieldValue$fieldData3 === void 0 ? void 0 : _fieldValue$fieldData3.dataKey) !== null && _fieldValue$fieldData2 !== void 0 ? _fieldValue$fieldData2 : null;
              const sourceDefinition = inputSource.outputFieldDefinitions.find(({
                typeDefinition
              }) => typeDefinition.name === sourceName);
              const sourceLabel = get(['typeDefinition', 'label'], sourceDefinition) || sourceName;
              return /*#__PURE__*/_jsx(FormattedReactMessage, {
                message: "automation-ui-action-features.action-features.extensions.action-detail.data-passing",
                options: {
                  sourceValue: /*#__PURE__*/_jsx(Text, {
                    as: "span",
                    typography: "heading-50",
                    children: sourceLabel
                  }),
                  sourceField: /*#__PURE__*/_jsx(Text, {
                    as: "span",
                    typography: "heading-50",
                    children: getActionLabelById({
                      actionId: sourceActionId
                    })
                  })
                }
              });
            }
          default:
            reportError({
              error: new Error(`Unrecognized valueType: ${fieldValue.valueType}`)
            });
            return null;
        }
      }
    }
    const {
      automationFieldType,
      typeDefinition: {
        externalOptionsReferenceType,
        fieldType,
        options,
        referencedObjectType,
        type
      }
    } = fieldDefinition;
    const labelMap = options.reduce((map, option) => {
      map[option.value || ''] = option.label || `${option.value}`;
      return map;
    }, {});
    return /*#__PURE__*/_jsx(FieldString, {
      automationFieldType: automationFieldType || undefined,
      externalOptionsReferenceType:
      // NOTE(bheng): referencedObjectType is deprecated
      externalOptionsReferenceType || referencedObjectType || undefined,
      fieldType: getConvertedFieldType(fieldType, type),
      labelMap: labelMap,
      objectTypeId: flowObjectTypeId,
      value: getStringValues(genericActionField) || convertFieldDataToDataToken(genericActionField)
    });
  };
  let content = actionDefinition.actionCardContent || actionDefinition.actionName;
  content = applyInterpolation(content, 'appName' in actionDefinition ? {
    appName: actionDefinition.appName || undefined
  } : {});
  const stringElements = [];
  let startIndex = 0;
  let elementKeyCounter = 0;
  let execResult = fieldKeyRegex.exec(content);

  // Parse the content string and replace field keys with field label.
  // Example content: Enroll contact in {{ sequence }} as {{ sender }}
  while (execResult) {
    const endIndex = execResult.index;
    stringElements.push( /*#__PURE__*/_jsx("span", {
      children: content.substring(startIndex, endIndex)
    }, `${elementKeyCounter}-string`));
    const [__, rawFieldKey] = execResult;
    const fieldKey = rawFieldKey ? rawFieldKey.trim() : rawFieldKey;
    stringElements.push( /*#__PURE__*/_jsx("b", {
      children: renderFieldLabel(fieldKey)
    }, `${elementKeyCounter}-element`));
    elementKeyCounter++;
    startIndex = endIndex + rawFieldKey.length + 4; // 4 for {{}} characters
    execResult = fieldKeyRegex.exec(content);
  }
  const remainingContent = content.substring(startIndex, content.length);
  if (remainingContent) {
    stringElements.push( /*#__PURE__*/_jsx("span", {
      children: remainingContent
    }, `${elementKeyCounter}-string`));
  }
  return /*#__PURE__*/_jsx("div", {
    children: stringElements
  });
};
export default ExtensionActionDetail;